/**
 * Dashboard product analytics event names (PostHog + Mixpanel via trackEvent).
 * Single lexicon for the monorepo — import from `@lucien-ai/ts`.
 *
 * Prefer snake_case keys in event `properties` (e.g. conversation_id).
 */
export const dashboardProductEvents = {
    integrationsNotionConnectClicked: "dashboard_integrations_notion_connect_clicked",
    integrationsNotionDisconnectClicked: "dashboard_integrations_notion_disconnect_clicked",
    integrationsNotionResyncClicked: "dashboard_integrations_notion_resync_clicked",
    integrationsSlackConnectClicked: "dashboard_integrations_slack_connect_clicked",
    integrationsSlackDisconnectClicked: "dashboard_integrations_slack_disconnect_clicked",
    integrationsPostgresConnectClicked: "dashboard_integrations_postgres_connect_clicked",
    integrationsPostgresDisconnectClicked: "dashboard_integrations_postgres_disconnect_clicked",
    integrationsPostgresResyncClicked: "dashboard_integrations_postgres_resync_clicked",
    integrationsGcalConnectClicked: "dashboard_integrations_gcal_connect_clicked",
    integrationsGcalDisconnectClicked: "dashboard_integrations_gcal_disconnect_clicked",
    integrationsAnalyticsConnectClicked: "dashboard_integrations_analytics_connect_clicked",
    integrationsAnalyticsDisconnectClicked: "dashboard_integrations_analytics_disconnect_clicked",
    integrationsViewed: "dashboard_integrations_viewed",
    integrationsFilterSelected: "dashboard_integrations_filter_selected",
    integrationsSearchChanged: "dashboard_integrations_search_changed",
    integrationsOauthReturn: "dashboard_integrations_oauth_return",
    integrationsRequestIntegrationClicked: "dashboard_integrations_request_integration_clicked",
    integrationsRequestIntegrationSubmitted: "dashboard_integrations_request_integration_submitted",
    integrationsLearnSecurityClicked: "dashboard_integrations_learn_security_clicked",
    briefsViewFullClicked: "dashboard_briefs_view_full_clicked",
    briefsSectionTileClicked: "dashboard_briefs_section_tile_clicked",
    briefsSnapshotReadMoreToggled: "dashboard_briefs_snapshot_read_more_toggled",
    homeViewAllBriefsClicked: "dashboard_home_view_all_briefs_clicked",
    homeTalkCopilotClicked: "dashboard_home_talk_copilot_clicked",
    homeCopilotThreadClicked: "dashboard_home_copilot_thread_clicked",
    homeStartConversationClicked: "dashboard_home_start_conversation_clicked",
    briefsListCardClicked: "dashboard_briefs_list_card_clicked",
    briefsGenerateQueued: "dashboard_briefs_generate_queued",
    briefsCadenceFilterSelected: "dashboard_briefs_cadence_filter_selected",
    briefsSearchChanged: "dashboard_briefs_search_changed",
    briefsClearFiltersClicked: "dashboard_briefs_clear_filters_clicked",
    briefsLoadMoreClicked: "dashboard_briefs_load_more_clicked",
    briefDetailBackClicked: "dashboard_brief_detail_back_clicked",
    briefDetailOverviewClicked: "dashboard_brief_detail_overview_clicked",
    briefEmptyStateCtaClicked: "dashboard_brief_empty_state_cta_clicked",
    briefItemReadMoreToggled: "dashboard_brief_item_read_more_toggled",
    briefItemSourceClicked: "dashboard_brief_item_source_clicked",
    briefActionConnectIntegrationClicked: "dashboard_brief_action_connect_integration_clicked",
    notificationBriefOpened: "dashboard_notification_brief_opened",
    notificationDismissClicked: "dashboard_notification_dismiss_clicked",
    notificationDismissAllClicked: "dashboard_notification_dismiss_all_clicked",
    scheduleJoinClicked: "dashboard_schedule_join_clicked",
    copilotMessageSent: "dashboard_copilot_message_sent",
    copilotShareModalOpened: "dashboard_copilot_share_modal_opened",
    copilotShareOrgToggled: "dashboard_copilot_share_org_toggled",
    copilotShareMemberAdded: "dashboard_copilot_share_member_added",
    copilotShareParticipantRemoved: "dashboard_copilot_share_participant_removed",
    copilotContextDrawerHidden: "dashboard_copilot_context_drawer_hidden",
    copilotContextDrawerShown: "dashboard_copilot_context_drawer_shown",
    copilotExportClicked: "dashboard_copilot_export_clicked",
    copilotExportCompleted: "dashboard_copilot_export_completed",
    copilotExportFailed: "dashboard_copilot_export_failed",
    copilotActionsPanelOpened: "dashboard_copilot_actions_panel_opened",
    copilotActionsPanelClosed: "dashboard_copilot_actions_panel_closed",
    copilotSlackUnlinkClicked: "dashboard_copilot_slack_unlink_clicked",
    copilotSuggestionApplied: "dashboard_copilot_suggestion_applied",
    copilotSuggestedPromptsToggled: "dashboard_copilot_suggested_prompts_toggled",
    copilotConversationSelected: "dashboard_copilot_conversation_selected",
    copilotConversationsLoadMore: "dashboard_copilot_conversations_load_more",
    copilotConversationSearchCleared: "dashboard_copilot_conversation_search_cleared",
    copilotTaskModeEntered: "dashboard_copilot_task_mode_entered",
    copilotTaskModeExited: "dashboard_copilot_task_mode_exited",
    copilotMeetingPrepStarted: "dashboard_copilot_meeting_prep_started",
    copilotMeetingPrepConversationCreated: "dashboard_copilot_meeting_prep_conversation_created",
    copilotMeetingPrepFailed: "dashboard_copilot_meeting_prep_failed",
    onboardingWorkspaceSubmitted: "onboarding_workspace_submitted",
    navSidebarItemClicked: "dashboard_nav_sidebar_clicked",
    settingsMembersTabSelected: "dashboard_settings_members_tab_selected",
    settingsMemberInviteSent: "dashboard_settings_member_invite_sent",
    settingsInviteRevoked: "dashboard_settings_invite_revoked",
    settingsMemberRemoved: "dashboard_settings_member_removed",
    settingsMemberRoleChanged: "dashboard_settings_member_role_changed",
    settingsWorkspaceDetailsSaved: "dashboard_settings_workspace_details_saved",
    settingsWorkspaceTimezoneSaved: "dashboard_settings_workspace_timezone_saved",
    settingsWorkspaceTimezoneCleared: "dashboard_settings_workspace_timezone_cleared",
    settingsAccountProfileSaved: "dashboard_settings_account_profile_saved",
    settingsAccountPreferencesSaved: "dashboard_settings_account_preferences_saved",
    settingsSupportHelpClicked: "dashboard_settings_support_help_clicked",
    settingsSupportEmailClicked: "dashboard_settings_support_email_clicked",
    slackInviteLinkSucceeded: "dashboard_slack_invite_link_succeeded",
    slackInviteLinkFailed: "dashboard_slack_invite_link_failed",
    actionApproveClicked: "dashboard_action_approve_clicked",
    actionRejectClicked: "dashboard_action_reject_clicked",
    actionViewDetailsClicked: "dashboard_action_view_details_clicked",
};
/** Internal / cross-cutting client events (not UI feature clicks). */
export const systemProductEvents = {
    userIdentified: "user_identified",
};
export const onboardingProductEvents = {
    welcomeContinueClickedTosNotAccepted: "onboarding_welcome_continue_clicked_tos_not_accepted",
    welcomeContinueClickedTosAccepted: "onboarding_welcome_continue_clicked_tos_accepted",
    teammatesSkipped: "onboarding_teammates_skipped",
    teammatesInvited: "onboarding_teammates_invited",
};
